/* =============================================================================
   VIEWS / WALK-IN — Schnell-Eintrag für spontan eingelaufene Gäste
   =============================================================================
   openWalkInForm: Modal mit Name/Personen/Datum/Zeit/Bemerkung,
                   optional mit pre-selected Tisch (aus dem Occupancy-Tab).
   submitWalkIn: Legt die Reservierung an und öffnet den Assignment-Picker.
============================================================================ */

import { htmlEscape, todayISO, showToast } from '../utils/helpers.js';
import { closeModal } from '../utils/dom.js';
// S271 §A — harte Grenze inkl. Walk-Ins (occupiedSeatsInWindow statt nur Reservierungen).
import { tableExtraChairs, getCurrentSlotDuration, violatesClosingTime, occupiedSeatsInWindow, totalCapacity } from '../core/capacity.js';
import { timeToMinutes } from '../utils/helpers.js';
import {
  renderMailOptInBlock, applyEmailAutoDefaults, syncMailOptInCheckboxes,
  RESERVATION_MAIL_KINDS, RESERVATION_POSITIVE_KINDS
} from './admin/mail-optin-block.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { tables: [] };
}
function currentRestaurantIdLazy() {
  // Zentraler Resolver (F-04). Bindet die Tenant-ID an den eingeloggten User
  // (auth.getCurrentRestaurantIdSync) und fällt sauber auf die Config-Default
  // zurück, wenn der Adapter ausnahmsweise noch nicht initialisiert ist.
  if (window.App && typeof window.App.getCurrentRestaurantId === 'function') {
    return window.App.getCurrentRestaurantId();
  }
  if (typeof window.currentRestaurantId === 'function') return window.currentRestaurantId();
  return (window.AppConfig && window.AppConfig.defaultRestaurantId) || 'rest_default';
}

let _walkInPreselectTableId = null;
let _walkinModalIsAdminRes  = false;   // S60 / 3-Pfad: differenziert Submit-Pfad
// S220 — Anlage erst beim Picker-Accept (nie mittendrin): Formular-Eingaben
// zwischenparken (für „← Zurück (Gast-Daten)") + Doppel-Accept-Schutz.
let _walkinDraft = null;
let _walkinAcceptInFlight = false;

// REF-7b (S75) — Mail-Opt-In-State für Admin-Reservierung (nur dieser Pfad
// versendet Mails; reiner Walk-In bleibt mailfrei).
let _walkinMailEmail  = '';
let _walkinMailOptIn  = { confirmed: false, declined: false, cancelled: false, reminder: false, _touched: false };
function _resetWalkinMailOptIn() {
  _walkinMailEmail = '';
  _walkinMailOptIn = { confirmed: false, declined: false, cancelled: false, reminder: false, _touched: false };
}

export function walkinSetMailEmail(v) {
  _walkinMailEmail = String(v || '');
  _walkinMailOptIn = applyEmailAutoDefaults(_walkinMailOptIn, _walkinMailEmail, RESERVATION_POSITIVE_KINDS);
  syncMailOptInCheckboxes('walkinres', _walkinMailOptIn, !!_walkinMailEmail.trim());
}
export function walkinSetMailOptIn(kind, checked) {
  if (!(kind in _walkinMailOptIn)) return;
  _walkinMailOptIn[kind] = !!checked;
  _walkinMailOptIn._touched = true;
}

export function openWalkInForm(opts) {
  if (!window.AppConfig || !window.AppConfig.features || !window.AppConfig.features.walkIn) return;
  opts = opts || {};
  _walkInPreselectTableId = opts.preselectTableId || null;
  const state = getState();
  const now = new Date();
  const defaultHH = String(now.getHours()).padStart(2, '0');
  const defaultMM = String(Math.floor(now.getMinutes() / 30) * 30).padStart(2, '0');
  const dateVal = opts.date || todayISO();
  const timeVal = opts.time || `${defaultHH}:${defaultMM}`;
  const preselectTable = opts.preselectTableId ? state.tables.find(t => t.id === opts.preselectTableId) : null;
  const preselectHint = _buildPreselectHint(
    preselectTable
      ? { name: preselectTable.name, seats: preselectTable.seats, extraChairs: tableExtraChairs(preselectTable) }
      : null
  );
  // Kontext-Variante: Wenn von "Reservierung erfassen"-Quick-Action aufgerufen
  // (Session 20 / P7), Modal-Header und Intro-Text auf "Reservierung" umstellen.
  // S60 / 3-Pfad: Modus für submitWalkIn merken — Reservation vs. Walk-In.
  const isAdminReservation = !!opts.adminReservation;
  _walkinModalIsAdminRes = isAdminReservation;
  // S220 — Prefill für „← Zurück (Gast-Daten)" aus dem Picker: erhaltene Eingaben
  // wiederherstellen statt zurücksetzen. Ohne Prefill = frischer Modal-Open.
  const prefill = opts.prefill || null;
  if (prefill) {
    _walkinMailEmail = String(prefill.email || '');
    _walkinMailOptIn = prefill.mailOptIn
      ? { ...prefill.mailOptIn }
      : { confirmed: false, declined: false, cancelled: false, reminder: false, _touched: false };
  } else {
    _resetWalkinMailOptIn();
  }
  const modalTitle = _buildModalTitle(isAdminReservation);
  const modalIntro = _buildModalIntro(isAdminReservation);
  // REF-7b — Mail-Opt-In nur bei Voraus-Reservation (reiner Walk-In hat keinen
  // Mail-Kontext: Gast steht vor der Theke).
  // S218 Cut 1 — Cockpit-Reservierung: E-Mail nicht mehr hart Pflicht (emailRequired
  // false → kein HTML-`required`), stattdessen Entweder-Oder-Guard (E-Mail ODER
  // Telefon) im Submit. Der Mail-Opt-In-Block bleibt an die E-Mail gekoppelt: ohne
  // E-Mail keine Bestätigungs-/Storno-Mail (Hinweis rendert der Block selbst).
  const mailBlock = isAdminReservation ? renderMailOptInBlock({
    kinds: RESERVATION_MAIL_KINDS,
    email: _walkinMailEmail,
    optIn: _walkinMailOptIn,
    idPrefix: 'walkinres',
    emailHandler: 'walkinSetMailEmail',
    toggleHandler: 'walkinSetMailOptIn',
    showToggles: false,    // S88 C: keine Checkboxen — Server leitet Opt-in ab
    emailRequired: false   // S218 Cut 1: E-Mail optional (E-Mail ODER Telefon, Guard im Submit)
  }) : '';
  const html = `
    <div class="modal-header">
      <h2>${modalTitle}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <p style="color: var(--ink-soft); font-size: .9rem;">
      ${modalIntro}
    </p>
    ${preselectHint}
    <form onsubmit="submitWalkIn(event)">
      <div class="field-row">
        <div class="field">
          <label>Name (falls erfragt)</label>
          <input id="wi-name" type="text" placeholder="optional" value="${prefill ? htmlEscape(prefill.name || '') : ''}" autofocus>
        </div>
        <div class="field">
          <label>Anzahl Personen *</label>
          <input id="wi-guests" type="number" min="1" max="20" value="${(prefill && prefill.guests) ? prefill.guests : (preselectTable ? Math.min(preselectTable.seats + tableExtraChairs(preselectTable), 20) : 2)}" required>
        </div>
      </div>
      <div class="field-row">
        <div class="field">
          <label>Datum</label>
          <input id="wi-date" type="date" value="${dateVal}" required>
        </div>
        <div class="field">
          <label>Uhrzeit (Start)</label>
          <input id="wi-time" type="time" step="1800" value="${timeVal}" required>
        </div>
      </div>
      <div class="field">
        <label>Bemerkung</label>
        <input id="wi-notes" type="text" placeholder="z.B. Stammgast, Allergien..." value="${prefill ? htmlEscape(prefill.notes || '') : ''}">
      </div>
      ${isAdminReservation ? `
      <div class="field">
        <label>Telefon</label>
        <input id="wi-phone" type="tel" placeholder="z.B. 0176 1234567" value="${prefill ? htmlEscape(prefill.phone || '') : ''}">
      </div>
      <div style="font-size:.78rem;color:var(--ink-soft,#888);margin:-4px 0 8px;">
        Mindestens <strong>E-Mail oder Telefon</strong> angeben. Ohne E-Mail keine Mail-Bestätigung/Storno-Link.
      </div>` : ''}
      ${mailBlock}
      <div class="btn-row" style="margin-top: 12px;">
        <button type="submit" class="btn btn-primary">${preselectTable ? 'Walk-In an Tisch ' + htmlEscape(preselectTable.name) + ' setzen' : 'Weiter zur Tisch-Auswahl'}</button>
        <button type="button" class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
      </div>
    </form>
  `;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export async function submitWalkIn(e) {
  if (e && e.preventDefault) e.preventDefault();

  // S60 / phase_51 — 3-PFAD-ARCHITEKTUR:
  //   isAdminReservation=true  → klassische Voraus-Reservation in reservations
  //   isAdminReservation=false → spontaner Walk-In in table_occupancies
  // Beide Pfade nutzen dasselbe Modal, Submit verzweigt am Modus.
  const isAdminReservation = _walkinModalIsAdminRes;

  const guests   = parseInt(document.getElementById('wi-guests').value, 10);
  const date     = document.getElementById('wi-date').value;
  const time     = document.getElementById('wi-time').value;
  const name     = document.getElementById('wi-name').value.trim();
  const notes    = document.getElementById('wi-notes').value.trim();
  const duration = getCurrentSlotDuration();

  // Sicherheitscheck 1: weder Reservation noch Walk-In dürfen über die Schließzeit hinausgehen.
  const overrun = violatesClosingTime(time, duration);
  if (overrun) {
    showToast(_buildOverrunErrorMsg(overrun, isAdminReservation), 'error');
    return;
  }
  // Sicherheitscheck 2: harte Kapazitätsgrenze.
  const startMin = timeToMinutes(time);
  const endMin   = startMin + duration;
  const currentMax = occupiedSeatsInWindow(date, startMin, endMin);
  const totalCap   = totalCapacity();
  if (!_isCapacityOk(currentMax, guests, totalCap)) {
    showToast(_buildCapacityErrorMsg(currentMax, totalCap, guests, isAdminReservation), 'error');
    return;
  }

  // S220 — Prinzip „Anlage erst beim finalen Accept": KEIN create mehr VOR dem
  // Picker (weder Reservierung noch Belegung). Formulardaten wandern in den Draft,
  // der Picker öffnet mit synthetischem Kontext (reservierungsfrei); erst der
  // Picker-Accept legt an. Abbrechen/×/ESC/Backdrop hinterlassen so nichts.
  if (isAdminReservation) {
    // S218 — Entweder-Oder-Pflicht: E-Mail ODER Telefon (mind. eins).
    const emailVal = (_walkinMailEmail || '').trim();
    const phoneEl  = document.getElementById('wi-phone');
    const phone    = phoneEl ? phoneEl.value.trim() : '';
    if (!_hasReservationContact(emailVal, phone)) {
      showToast('Bitte E-Mail oder Telefon für die Reservierung angeben.', 'error');
      return;
    }
    _walkinDraft = {
      isAdminReservation: true,
      name, guests, date, time, notes, phone,
      email: emailVal || null,
      duration,
      mailOptIn: { ..._walkinMailOptIn }
    };
    const ctx = {
      id: 'walkinres-pick',
      label: 'reservation',          // Picker-Optik: „Reservierung bestätigen" + Datum/Uhrzeit
      name: name || 'Anonym',
      // S221 — kein E-Mail-abhängiges Accept-Label mehr (Button heißt immer
      // „Bestätigen"); die E-Mail lebt nur noch im Draft (createAdmin).
      date, time, guests, area: 'any', durationMin: duration,
      assignedTables: [],
      onAccept: (tables, chairs) => _acceptAdminReservation(tables, chairs),
      onBack:   () => _reopenWalkinForm(),   // „← Zurück (Gast-Daten)"
      onClose:  () => _cancelWalkin()        // ×/Abbrechen = alles verwerfen
    };
    if (typeof window.openAssignmentPicker === 'function') {
      window.openAssignmentPicker(ctx.id, 'confirm', ctx);
    }
    return;
  }

  // ===== PFAD 2: spontaner Walk-In (table_occupancies) =====
  const occAdapter = window.App && window.App.storage && window.App.storage.tableOccupancies;
  if (!occAdapter || typeof occAdapter.create !== 'function') {
    showToast('Walk-In-Adapter nicht initialisiert — Cockpit neu laden.', 'error');
    return;
  }
  const preId = _walkInPreselectTableId;
  _walkinDraft = { isAdminReservation: false, name, guests, date, time, notes, duration, preselectTableId: preId };
  const ctx = {
    id: 'walkin-pick',
    label: 'walkin',
    name: name || 'Walk-In',
    date, time, guests, area: 'any', durationMin: duration,
    assignedTables: preId ? [preId] : [],   // Belegungs-Kachel-Einstieg: Tisch vorgewählt
    onAccept: (tables, chairs) => _acceptWalkin(tables, chairs),
    onBack:   () => _reopenWalkinForm(),
    onClose:  () => _cancelWalkin()
  };
  if (typeof window.openAssignmentPicker === 'function') {
    window.openAssignmentPicker(ctx.id, 'walkin', ctx);
  }
}

/** S220 — „← Zurück (Gast-Daten)": Formular mit erhaltenen Eingaben neu öffnen. */
function _reopenWalkinForm() {
  const d = _walkinDraft || {};
  openWalkInForm({
    adminReservation: !!d.isAdminReservation,
    date: d.date, time: d.time,
    preselectTableId: d.preselectTableId || null,
    prefill: { name: d.name, guests: d.guests, notes: d.notes, phone: d.phone, email: d.email, mailOptIn: d.mailOptIn }
  });
}

/** S220 — Abbrechen/×/ESC: alles verwerfen (es wurde noch nichts angelegt). */
function _cancelWalkin() {
  _walkinDraft = null;
  _walkinModalIsAdminRes = false;
  _walkInPreselectTableId = null;
  closeModal();
}

/**
 * S220 Cut 1 — Picker-Accept (Cockpit-Reservierung): JETZT erst anlegen. Zweistufig
 * createAdmin(pending) → commitAssignmentAndConfirm: die Confirm-Mail feuert erst auf
 * dem UPDATE mit vollständigen Daten (kein leerer Storno-Link, Memory
 * gg-confirmed-mail-insert-timing). createAdmin patcht den lokalen Cache synchron →
 * commitAssignmentAndConfirm findet die Reservierung sofort.
 */
async function _acceptAdminReservation(tables, chairs) {
  if (_walkinAcceptInFlight) return;
  _walkinAcceptInFlight = true;
  try {
    const d = _walkinDraft || {};
    const req = {
      name:   d.name || 'Anonym',
      phone:  d.phone || '',
      email:  d.email || null,
      date: d.date, time: d.time, guests: d.guests,
      area:   'any',
      notes:  d.notes || '',
      preorder: [],
      status: 'pending',
      assignedTables: [],
      serviceType: 'dine-in',
      durationMin: d.duration,
      mailOptIn: d.mailOptIn || {}
    };
    let created;
    try {
      created = await window.App.storage.reservations.createAdmin(req);
    } catch (err) {
      console.error('[walkin] createAdmin (Accept) failed:', err);
      showToast(_buildWalkinSaveErrorMsg(err, true), 'error');
      return;
    }
    try { await window.App.events.emit('reservation:created', created); }
    catch (emitErr) { console.warn('[walkin] events.emit failed (entity persisted):', emitErr); }
    _walkinDraft = null;
    _walkinModalIsAdminRes = false;
    if (typeof window.commitAssignmentAndConfirm === 'function') {
      await window.commitAssignmentAndConfirm(created.id, tables, Number(chairs) || 0, 'confirm');
    } else {
      closeModal();
    }
  } finally {
    _walkinAcceptInFlight = false;
  }
}

/**
 * S220 Cut 2 — Picker-Accept (Walk-In): Belegung JETZT erst anlegen, inkl.
 * Doppel-Submit-Schutz (checkConflict → Sharing-Rückfrage, Memory
 * gg-walk-in-double-submit). Abbruch der Sharing-Rückfrage legt nichts an.
 */
async function _acceptWalkin(tables, chairs) {
  if (_walkinAcceptInFlight) return;
  if (!Array.isArray(tables) || !tables.length) return;
  _walkinAcceptInFlight = true;
  try {
    const d = _walkinDraft || {};
    const state = getState();
    const primaryId = tables[0];
    const occAdapter = window.App && window.App.storage && window.App.storage.tableOccupancies;
    if (!occAdapter || typeof occAdapter.create !== 'function') {
      showToast('Walk-In-Adapter nicht initialisiert — Cockpit neu laden.', 'error');
      return;
    }
    // Doppel-Submit-/Sharing-Schutz (analog submitDineInSeatOnly).
    try {
      const conflict = await occAdapter.checkConflict(primaryId);
      if (conflict && conflict.has_conflict && conflict.occupancy) {
        const occ = conflict.occupancy;
        const min = Math.round(Number(occ.minutes_since_start) || 0);
        const t = (state.tables || []).find(x => x.id === primaryId);
        const tableName = (t && t.name) || primaryId;
        const guest = occ.guest_name ? ` (${occ.guest_name})` : '';
        const msg = `⚠️ Tisch ${tableName} ist BEREITS belegt: ${occ.guests} Gäste${guest}, belegt seit ${min} Min.`;
        if (!(await window.styledConfirm({ title: 'Trotzdem zweite Belegung (Sharing)?', body: msg, danger: true, confirmLabel: 'Sharing anlegen' })).confirmed) {
          return;   // Abbruch — nichts angelegt, Picker bleibt offen
        }
      }
    } catch (checkErr) {
      console.warn('[walkin] checkConflict fehlgeschlagen, fahre fort:', checkErr);
    }
    let created;
    try {
      created = await occAdapter.create({
        tableId:          primaryId,
        assignedTableIds: tables.slice(),
        guests:           d.guests,
        guestName:        d.name || null,
        durationMin:      d.duration,
        notes:            d.notes || null
      });
    } catch (err) {
      console.error('[walkin] tableOccupancies.create (Accept) failed:', err);
      showToast('Konnte Walk-In nicht anlegen: ' + (err.message || err), 'error');
      return;
    }
    try { await window.App.events.emit('walkin:created', created); }
    catch (emitErr) { console.warn('[walkin] events.emit failed (entity persisted):', emitErr); }
    _walkinDraft = null;
    _walkInPreselectTableId = null;
    const t = (state.tables || []).find(x => x.id === primaryId);
    const tableName = (t && t.name) || primaryId;
    showToast(`Tisch ${tableName} belegt (${d.guests}P · ${d.duration} Min).`, 'success');
    closeModal();
    if (typeof window.renderOccupancy === 'function') window.renderOccupancy();
  } finally {
    _walkinAcceptInFlight = false;
  }
}

// ---------------------------------------------------------------
// Pure helpers (extrahiert in Session 39 / B.2 für Test-Coverage)
// ---------------------------------------------------------------

function _buildModalTitle(isAdminReservation) {
  return isAdminReservation ? 'Reservierung erfassen' : 'Walk-In erfassen';
}

function _buildModalIntro(isAdminReservation) {
  return isAdminReservation
    ? 'Geplante Reservierung für die Zukunft. Tische wählst du im nächsten Schritt aus.'
    : 'Schneller Eintrag für spontan eingelaufene Gäste. Tische wählst du im nächsten Schritt aus.';
}

/**
 * @param {{name: string, seats: number, extraChairs: number} | null} table
 * @returns {string} HTML-Snippet oder leerer String, wenn kein Tisch vorausgewählt
 */
function _buildPreselectHint(table) {
  if (!table) return '';
  const safeName = htmlEscape(String(table.name || ''));
  const seats = Number(table.seats) || 0;
  const extra = Number(table.extraChairs) || 0;
  const extraStr = extra > 0 ? ` +${extra}🪑` : '';
  return (
    `<div class="notes" style="background:var(--accent-soft, var(--bg-soft)); margin-bottom:12px; padding:8px 12px;">` +
    `\n         🎯 <strong>Tisch ${safeName}</strong> (${seats}P${extraStr}) wird nach dem Speichern vorgewählt.` +
    `\n       </div>`
  );
}

/** S134c (Befund 2) — Subjekt der Fehlermeldungen kontextabhängig:
 *  Reservierung (Voraus) vs. Walk-In (spontan). */
function _entrySubject(isAdminReservation) {
  return isAdminReservation ? 'Die Reservierung' : 'Der Walk-In';
}

/**
 * @param {{endStr: string, closeStr: string}} overrun
 * @param {boolean} [isAdminReservation]
 */
function _buildOverrunErrorMsg(overrun, isAdminReservation) {
  const endStr = (overrun && overrun.endStr) || '?';
  const closeStr = (overrun && overrun.closeStr) || '?';
  return `${_entrySubject(isAdminReservation)} würde um ${endStr} enden — nach Ladenschluss um ${closeStr}. Bitte frühere Uhrzeit wählen.`;
}

/** S218 Cut 1 — Cockpit-Reservierung: mindestens EIN Kontaktweg (E-Mail ODER
 *  Telefon) muss angegeben sein. Pure (Vitest). Whitespace zählt nicht. */
function _hasReservationContact(email, phone) {
  return !!(String(email || '').trim() || String(phone || '').trim());
}

function _isCapacityOk(currentMax, guests, totalCap) {
  const a = Number(currentMax) || 0;
  const b = Number(guests) || 0;
  const c = Number(totalCap) || 0;
  return a + b <= c;
}

function _buildCapacityErrorMsg(currentMax, totalCap, guests, isAdminReservation) {
  const a = Number(currentMax) || 0;
  const b = Number(guests) || 0;
  const c = Number(totalCap) || 0;
  return `${_entrySubject(isAdminReservation)} würde die Kapazität überschreiten: ${a} bereits simultan anwesende Gäste + ${b} neue = ${a + b} > Max. ${c} Plätze.`;
}

/**
 * Erzeugt eine Bediener-freundliche Fehlermeldung für DB-Save-Failures
 * im submitWalkIn-Pfad (H-04 Härtung, Session 43).
 *
 * @param {unknown} err - Original-Error aus dem reservations.create-Reject
 * @returns {string}
 */
function _buildWalkinSaveErrorMsg(err, isAdminReservation) {
  const detail = (err && typeof err === 'object' && 'message' in err && err.message)
    ? String(err.message)
    : (typeof err === 'string' ? err : 'Netzwerk- oder DB-Fehler');
  return `${_entrySubject(isAdminReservation)} konnte nicht gespeichert werden: ${detail}. Bitte erneut versuchen.`;
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, { openWalkInForm, submitWalkIn, walkinSetMailEmail, walkinSetMailOptIn });
}

// ---------------------------------------------------------------
// Test-Hook-Exports (Session 39, Pattern aus customer-form.js).
// Zur Laufzeit No-Op.
// ---------------------------------------------------------------
export {
  _buildModalTitle,
  _buildModalIntro,
  _buildPreselectHint,
  _buildOverrunErrorMsg,
  _isCapacityOk,
  _buildCapacityErrorMsg,
  _buildWalkinSaveErrorMsg,
  _hasReservationContact,
};
